import {Component, OnDestroy, OnInit} from '@angular/core';
import {filter, first, switchMap, takeUntil} from "rxjs/operators";
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {User} from "../user/user";
import * as fromStore from '../store';
import {Store} from '@ngrx/store';
import {combineLatest} from 'rxjs';
import {UserSubscription, UserSubscriptionType} from "../user/user-subscription";
import {IDropdownListItem} from "../shared/components/dropdown-list/dropdown-list-item";
import {FormControl} from "@angular/forms";
import {UserManagementService} from "./user-management.service";
import {DialogService} from "../../ng2-bootstrap-modal/dialog.service";
import {
  LeaveRemoveWorkspaceDialogComponent
} from "../shared/components/dialogs/leave-remove-workspace-dialog/leave-remove-workspace-dialog.component";
import {
  ManageWorkspaceUsersDialogComponent
} from "../shared/components/dialogs/manage-workspace-users-dialog/manage-workspace-users-dialog.component";
import {DbResponse} from "../shared/interceptors/db-response";
import {Subject} from "rxjs";
import {WorkspaceClient} from "./workspace-client";
import {clientRoles} from "../user/user-roles-actions";
import {NotifierComponent} from "../shared/components/notifier/notifier.component";
import {Notification} from "../shared/components/notifier/notification";
import {NotificationType} from "../shared/components/notifier/notification-type";
import {Location} from "@angular/common";
import {TypeUtils} from "../shared/utils/type-utils";
import {Config} from "../config/config";
import {GoogleAnalyticsService} from "../shared/services/google-analytics/google-analytics.service";
import {ConfirmDialogComponent} from "../shared/components/dialogs/confirm-dialog/confirm-dialog.component";
import {TextUtils} from "../shared/utils/text-utils";
import {DbModalService} from "../modules/modal/modal";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {Utils} from "../shared/utils/utils";
import {
  PricingPageViewComponent
} from "../shared/components/dialogs/one-month-free/components/pricing-page-view/pricing-page-view.component";


@Component({
    selector: 'db-user-management',
    templateUrl: './user-management.component.html',
    styleUrls: ['./user-management.component.scss'],
    standalone: false
})
export class UserManagementComponent implements OnInit, OnDestroy {

  public user: User;
  public userSubscriptionType = UserSubscriptionType;
  public removeItem: IDropdownListItem[];
  public loading: boolean;
  public resendLoading: boolean;
  public clientId: number = 0;
  public searchTerm: FormControl;
  public workspaceClients: any[] = [];
  public filteredWorkspaceClients: any[] = [];
  public clientRoles = clientRoles;
  public isCurrentWorkspaceInActive: boolean;
  public screen = window;
  public currentSubscription: UserSubscription;

  private workspaceName: string = '';
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  public Config = Config;
  demoUser = false;

  constructor(
    private _store: Store<fromStore.DbStoreState>,
    private _userManagementService: UserManagementService,
    private _dialogService: DialogService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _location: Location,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _modalService: DbModalService,
  ) { }

  ngOnInit() {
    this.searchTerm = new FormControl('');
    this.addSubscriptions();
    this.workspaceClients = this._route.snapshot.data['workspace'].workspaceClients;
    this.workspaceName = this._route.snapshot.data['workspace'].workspaceName || '';
    this.removeItem = [
      {
        icon: 'plugins',
        label: 'Remove user',
        action: (client) => {
          this.removeUser(client);
        }
      }
    ];
    this._route.queryParams
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(param => {
        param['from'] && this.inviteUsers(param['from']);
        if (!TypeUtils.isEmpty(param)) {
          // Replace the current state in the history stack
          history.replaceState({}, '', this._router.url.split('?')[0]);

          this._router.navigate([], {
            relativeTo: this._route,
            queryParams: {}
          });
        }
      });
    this._router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        if (event.urlAfterRedirects !== '/user-management') {
          this._dialogService.removeAll();
        }
      }
    });
  }

  ngOnDestroy() {
    this._destroyStream$.next(true);
    this._destroyStream$.complete();
  }

  private addSubscriptions(): void {
    this._store.select(fromStore.getWorkspaceLoaded)
      .pipe(
        filter(loaded => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getWorkspaceAllClients),
          this._store.select(fromStore.getOwnerWorkspace),
          this._store.select(fromStore.getUserSubscription)
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, clients, ownerWorkspace, currentSubscription]) => {
        this.demoUser = Utils.isDemoUser(currentSubscription, user);
        this.user = user;
        this.workspaceClients = clients;
        this.isCurrentWorkspaceInActive = (ownerWorkspace && ownerWorkspace.status === 'inactive') || (currentSubscription && ([<string>UserSubscriptionType.ONE_PRODUCT, <string>UserSubscriptionType.PREMIUM]).includes(currentSubscription.type));
        this.workspaceName = ownerWorkspace && ownerWorkspace.workspaceName;
        this.currentSubscription = currentSubscription;
        this.searchUsers();
      });

    this.searchTerm.valueChanges
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(value => this.searchUsers(value));
  }

  public inviteUsers(_from?: 'header' | 'workspace_popup'): void {
    // TODO find a better place to call this function
    if (this.user && this.user.subscriptionId === Config.defaultStarterPlanId) {
      return this.showCheckoutDialog();
    }
    this._dialogService.addDialog(ManageWorkspaceUsersDialogComponent, {
      isInvite: true,
      ownerEmail: this.user.email,
      paidClient: this.user.planType !== this.userSubscriptionType.FREE,
      isAgency: this.currentSubscription && this.currentSubscription.plan && this.currentSubscription.plan.title.toLowerCase().trim() === 'agency',
      userRole: this.user.signUpFor && this.user.signUpFor.Role && this.user.signUpFor.Role.id,
      from: _from,
      currentSubscription: this.currentSubscription
    });
  }

  updateUser(client: WorkspaceClient) {
    this._dialogService.addDialog(ManageWorkspaceUsersDialogComponent, {
      isInvite: false,
      ownerEmail: this.user.email,
      currentSubscription: this.currentSubscription,
      userData: {
        name: client.name,
        email: client.email,
        clientId: client.id,
        role: client.role.roleId,
        domains: client.domains
      }
    });
  }

  public removeUser(data) {
    this._dialogService
      .addDialog(LeaveRemoveWorkspaceDialogComponent, {
        leaveWorkspace: false,
        user: data.client,
        workspaceName: this.workspaceName
      })
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe(confirmed => {
        confirmed && this.workspaceClients.splice(data.index, 1);
      });
  }

  public redirectToUpgrade(): void {
    if (this.currentSubscription && this.currentSubscription.trialBoosterClient) {
      const analyticsData = {
        eventCategory: 'Booster Upgrade Offer',
        eventAction: "Choose plan pop up",
        eventLabel: "View"
      };
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
      this._dialogService.addDialog(ConfirmDialogComponent, {
        header: TextUtils.getText('text_buyNowDialog.header'),
        message: TextUtils.getText('text_buyNowDialog.message'),
        confirmButtonText: TextUtils.getText('text_buyNowDialog.text'),
        confirmButtonClass: 'ds-btn-blue'
      }).subscribe(redirect => {
        if (redirect) {
          const _analyticsData = {
            eventCategory: 'Booster Upgrade Offer',
            eventAction: "Choose plan pop up",
            eventLabel: "Button click"
          };
          this._googleAnalyticsService.sendAnalyticsEventWithCore(_analyticsData);
          this._router.navigate(['/upgrade-plan']);
        }
      });
    } else {
      this._router.navigate(['/upgrade-plan']);
    }
  }

  public resendInvitation(workspaceInvitationId: number): void {
    this.resendLoading = true;
    this.clientId = workspaceInvitationId;
    this._userManagementService.resendInvitation(workspaceInvitationId)
      .pipe(filter((response: DbResponse): boolean => !!response),
      first())
      .subscribe(response => {
        this.resendLoading = false;
        if (response.success) {
          NotifierComponent.notify(new Notification("Email Sent", NotificationType.SUCCESS));
        } else {
          NotifierComponent.notify(new Notification(response.message, NotificationType.DANGER));
        }
      });
  }

  private searchUsers(value?: string): void {
    if (!value) {
      this.filteredWorkspaceClients = this.workspaceClients;
    } else {
      this.filteredWorkspaceClients = this.workspaceClients.filter(client => {
        return client.email.includes(value) || client.name && client.name.includes(value);
      });
    }
  }

  private showCheckoutDialog() {
    const analyticsData = {
      eventCategory: this.demoUser ? 'Free Upgrade Offer' : 'Free trial paywalls',
      eventAction: "Locked button click",
      eventLabel: "Invite User"
    };
    if (this.demoUser) {
      const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
      const options = {
        overflowHidden: false,
        backdropClass: 'blurred',
      };
      if (!firstTime) {
        options['width'] = 1240;
      } else {
        options['width'] = 460;
        options['height'] = 600;
      }
      const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
      modal.componentInstance.showFirstTime = firstTime;
    }
    else {
      this._modalService.open(PricingPageViewComponent, { overflowHidden: true, height: 960, width: 1076});
    }
    this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
  }
}
